import { Stack, Skeleton, Divider, Group, Grid, Box } from 'components'

interface ProjectViewSkeletonProps {
  isArcMosaico?: boolean
}

/**
 * Apple-style skeleton for ProjectView
 * Mirrors the actual content structure with realistic proportions
 */
export const ProjectViewSkeleton = ({ isArcMosaico = false }: ProjectViewSkeletonProps) => {
  if (isArcMosaico) {
    return <ProjectViewSkeletonArc />
  }

  return <ProjectViewSkeletonLegacy />
}

/**
 * Arc Mosaico specific skeleton
 * Matches the Arc project detail view structure
 */
const ProjectViewSkeletonArc = () => (
  <Stack spacing='md'>
    {/* Header Section - Title + Badge + Date */}
    <Grid>
      <Grid.Col span={12} sm={7}>
        <Stack spacing='sm'>
          <Stack spacing={8}>
            <Skeleton height={36} width='65%' radius='md' />
            <Skeleton height={22} width={55} radius='xl' />
          </Stack>
          <Skeleton height={14} width={200} radius='sm' />
        </Stack>
      </Grid.Col>
      <Grid.Col span={12} sm={5}>
        <Group spacing='sm' position='right'>
          <Skeleton height={42} width={135} radius='md' />
          <Skeleton height={42} width={145} radius='md' />
        </Group>
      </Grid.Col>
    </Grid>

    <Divider />

    {/* Lessons count + Arc Mosaico badge */}
    <Group position='apart'>
      <Stack spacing={4}>
        <Skeleton height={12} width={55} radius='sm' />
        <Skeleton height={16} width={75} radius='sm' />
      </Stack>
      <Group spacing='xs'>
        <Skeleton height={32} width={32} radius='xl' />
        <Skeleton height={16} width={85} radius='sm' />
      </Group>
    </Group>

    {/* Tab */}
    <Box>
      <Skeleton height={36} width={110} radius='sm' mb={8} />
      <Skeleton height={3} width={110} radius='xl' />
    </Box>

    {/* Content Sections */}
    <Stack spacing={40} mt='lg'>
      {/* Essential Question */}
      <SectionSkeleton titleWidth={160} lines={2} lineHeight={18} />

      {/* Summary */}
      <SectionSkeleton titleWidth={90} lines={5} lineHeight={18} />

      {/* Learning Goals - List style */}
      <Stack spacing='md'>
        <Skeleton height={26} width={140} radius='sm' />
        <Stack spacing={12}>
          {[95, 80, 88, 72, 85].map((width, i) => (
            <Group key={i} spacing='sm' noWrap align='flex-start'>
              <Skeleton height={6} width={6} radius='xl' mt={6} />
              <Skeleton height={18} width={`${width}%`} radius='sm' />
            </Group>
          ))}
        </Stack>
      </Stack>

      {/* Deliverable */}
      <SectionSkeleton titleWidth={110} lines={4} lineHeight={18} />

      {/* Skills & Standards */}
      <Stack spacing='md'>
        <Skeleton height={26} width={280} radius='sm' />
        <Stack spacing={12}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={80} radius='md' />
          ))}
        </Stack>
      </Stack>

      {/* Lessons section title */}
      <Stack spacing='md'>
        <Skeleton height={26} width={100} radius='sm' />
        <Skeleton height={120} radius='md' />
        <Skeleton height={120} radius='md' />
      </Stack>
    </Stack>
  </Stack>
)

/**
 * Legacy skeleton for non-Arc views
 */
const ProjectViewSkeletonLegacy = () => (
  <Stack spacing='md'>
    <Stack spacing='xs'>
      <Skeleton height={40} width={200} radius='md' />
      <Skeleton height={20} width={100} radius='sm' />
    </Stack>
    <Divider />
    <Stack spacing='xs'>
      <Skeleton height={40} radius='md' />
      <Skeleton height={20} radius='sm' />
    </Stack>
  </Stack>
)

/**
 * Reusable section skeleton with title and paragraph lines
 */
const SectionSkeleton = ({
  titleWidth,
  lines,
  lineHeight = 16,
}: {
  titleWidth: number
  lines: number
  lineHeight?: number
}) => (
  <Stack spacing='md'>
    <Skeleton height={26} width={titleWidth} radius='sm' />
    <Stack spacing={10}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={lineHeight}
          width={i === lines - 1 ? '70%' : '100%'}
          radius='sm'
        />
      ))}
    </Stack>
  </Stack>
)
